import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery, gql } from '@apollo/client'
import { Link as RouterLink } from 'react-router-dom'
import Dialog from 'components/dialog'
import Typography from '@material-ui/core/Typography'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import Divider from '@material-ui/core/Divider'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { makeStyles } from '@material-ui/core/styles'
import EventOutlinedIcon from 'components/material-symbol/icons/Event'
import CalendarTodayOutlinedIcon from 'components/material-symbol/icons/CalendarToday'
import MailOutlineIcon from 'components/material-symbol/icons/MailOutline'
import FileCopyOutlinedIcon from 'components/material-symbol/icons/FileCopy'
import LinkIcon from 'components/material-symbol/icons/Link'
import CheckCircleOutlineIcon from 'components/material-symbol/icons/CheckCircleOutline'
import CheckBoxOutlineBlankIcon from 'components/material-symbol/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from 'components/material-symbol/icons/CheckBox'
import CopyTextButton from 'components/copy-text-button'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import { API_ROOT } from 'env'
import { gtmEvent } from 'lib/gtm'
import { useAuth } from 'lib/auth'

const WEBCAL_QUERY = gql`
  query WebcalQuery {
    currentUser {
      externalId
    }
  }
`

const CALENDAR_SYNC_STATUS = gql`
  query CalendarSyncStatusForSubscribe {
    currentUser {
      attributes {
        ... on Coach {
          calendarSyncConnections {
            id
            provider
          }
        }
      }
    }
  }
`

const useStyles = makeStyles(theme => ({
  titleRoot: {
    paddingBottom: theme.spacing(1),
  },
  optionGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  optionLabel: {
    alignItems: 'center',
    marginLeft: 0,
    marginRight: 0,
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 1.5, 1.5, 1),
    borderRadius: 12,
    backgroundColor: '#F5F5F5',
  },
  optionCheckbox: {
    padding: theme.spacing(0.75),
    marginRight: theme.spacing(0.5),
    color: '#40484C',
    flexShrink: 0,
    '& svg, & .MuiSvgIcon-root': {
      fontSize: 24,
    },
  },
  optionCheckboxChecked: {
    color: '#0053FF',
  },
  optionText: {
    minWidth: 0,
    marginLeft: theme.spacing(0.5),
  },
  optionTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.25,
    color: '#172024',
  },
  optionDescription: {
    display: 'block',
    marginTop: 2,
    fontSize: '0.75rem',
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
  },
  sectionLabel: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  destinationCard: {
    height: '100%',
    display: 'block',
    borderRadius: 12,
    boxShadow: 'none',
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create(['border-color', 'background-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  destinationContent: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    height: '100%',
    boxSizing: 'border-box',
    gap: theme.spacing(1.5),
  },
  destinationIcon: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: theme.palette.primary.selected,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.main,
  },
  destinationText: {
    minWidth: 0,
  },
  destinationTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
  },
  destinationSubtitle: {
    display: 'block',
    marginTop: 2,
    fontSize: '0.75rem',
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
  },
  feedDivider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  feedFooter: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  feedIcon: {
    flexShrink: 0,
    marginRight: theme.spacing(1),
    fontSize: 18,
    color: theme.palette.text.secondary,
  },
  feedUrl: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.75rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  connectedIcon: {
    color: '#4caf50',
    fontSize: 16,
    marginLeft: theme.spacing(0.5),
    verticalAlign: 'middle',
  },
}))

const checkboxIcon = <CheckBoxOutlineBlankIcon />
const checkboxCheckedIcon = <CheckBoxIcon fill={1} />

const IncludeOption = ({ checked, onChange, label, description }) => {
  const classes = useStyles()

  return (
    <FormControlLabel
      className={classes.optionLabel}
      control={
        <Checkbox
          checked={checked}
          onChange={onChange}
          color="default"
          icon={checkboxIcon}
          checkedIcon={checkboxCheckedIcon}
          classes={{
            root: classes.optionCheckbox,
            checked: classes.optionCheckboxChecked,
          }}
        />
      }
      label={
        <Box className={classes.optionText}>
          <Typography className={classes.optionTitle}>{label}</Typography>
          {description && (
            <Typography className={classes.optionDescription}>
              {description}
            </Typography>
          )}
        </Box>
      }
    />
  )
}

IncludeOption.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
}

const DestinationCard = ({
  icon,
  title,
  subtitle,
  href,
  to,
  onClick,
  connected,
  component: Component,
  ...componentProps
}) => {
  const classes = useStyles()

  const content = (
    <Box className={classes.destinationContent}>
      <Box className={classes.destinationIcon}>{icon}</Box>
      <Box className={classes.destinationText}>
        <Typography className={classes.destinationTitle}>
          {title}
          {connected && (
            <CheckCircleOutlineIcon className={classes.connectedIcon} />
          )}
        </Typography>
        <Typography className={classes.destinationSubtitle}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  )

  if (Component) {
    return (
      <Component
        className={classes.destinationCard}
        component={Card}
        elevation={0}
        {...componentProps}
        onClick={onClick}
      >
        {content}
      </Component>
    )
  }

  if (to) {
    return (
      <Card
        className={classes.destinationCard}
        elevation={0}
        component={RouterLink}
        to={to}
        onClick={onClick}
      >
        {content}
      </Card>
    )
  }

  return (
    <Card
      className={classes.destinationCard}
      elevation={0}
      component="a"
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={onClick}
    >
      {content}
    </Card>
  )
}

DestinationCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  href: PropTypes.string,
  to: PropTypes.string,
  onClick: PropTypes.func,
  connected: PropTypes.bool,
  component: PropTypes.elementType,
}

const GoogleCalendarDestination = () => {
  const { data } = useQuery(CALENDAR_SYNC_STATUS)
  const connections =
    data?.currentUser?.attributes?.calendarSyncConnections || []
  const isConnected = connections.some(c => c.provider === 'google')

  return (
    <DestinationCard
      to="/pga-coach/settings/google-calendar"
      icon={<EventOutlinedIcon fontSize="small" color="primary" />}
      title="Google Calendar"
      subtitle={
        isConnected ? 'Real-time sync enabled' : 'Set up real-time sync'
      }
      connected={isConnected}
    />
  )
}

const WebcalLinks = ({
  includeBlockedTimes = false,
  includeJrlEvents = false,
  includeFgEvents = false,
}) => {
  const classes = useStyles()
  const { data, loading } = useQuery(WEBCAL_QUERY)

  if (loading || !data.currentUser?.externalId) {
    return null
  }

  const calUrl = new URL(
    `/coach/${
      data.currentUser.externalId
    }/webcal_feed?include_blocked_times=${!!includeBlockedTimes}&include_jrl_events=${!!includeJrlEvents}&include_fg_events=${!!includeFgEvents}`,
    API_ROOT,
  )
  const feedUrl = calUrl.toString()
  const webcalUrl = feedUrl.replace('https:', 'webcal:')

  const track = formAction =>
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'coach-schedule-subscribe-dialog',
      formAction,
    })

  return (
    <>
      <Typography className={classes.sectionLabel}>
        Choose a destination
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <GoogleCalendarDestination />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DestinationCard
            href={webcalUrl}
            icon={
              <CalendarTodayOutlinedIcon fontSize="small" color="primary" />
            }
            title="Your calendar app"
            subtitle="Apple Calendar, Fantastical, and more"
            onClick={() => track('calendar-app')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DestinationCard
            href={`https://outlook.live.com/owa?path=/calendar/action/compose&rru=addsubscription&url=${encodeURIComponent(
              webcalUrl,
            )}&name=PGA%20Coach`}
            icon={<MailOutlineIcon fontSize="small" color="primary" />}
            title="Outlook.com"
            subtitle="Subscribe from Microsoft 365"
            onClick={() => track('outlook-com')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DestinationCard
            component={CopyTextButton}
            value={feedUrl}
            notificationText="Calendar URL copied"
            notificationVariant="success"
            icon={<FileCopyOutlinedIcon fontSize="small" color="primary" />}
            title="Copy calendar URL"
            subtitle="Paste into any app that reads ICS"
            onClick={() => track('copy-url')}
          />
        </Grid>
      </Grid>
      <Divider className={classes.feedDivider} />
      <Box className={classes.feedFooter}>
        <LinkIcon className={classes.feedIcon} />
        <Typography className={classes.feedUrl} title={feedUrl}>
          {feedUrl}
        </Typography>
      </Box>
    </>
  )
}

WebcalLinks.propTypes = {
  includeBlockedTimes: PropTypes.bool,
  includeJrlEvents: PropTypes.bool,
  includeFgEvents: PropTypes.bool,
}

const SubscribeDialog = ({ trigger }) => {
  const dialogClasses = useDialogTitleStyles()
  const classes = useStyles()
  const [includeBlockedTimes, setIncludeBlockedTimes] = useState(false)
  const [includeJrlEvents, setIncludeJrlEvents] = useState(false)
  const [includeFgEvents, setIncludeFgEvents] = useState(false)
  const { user } = useAuth()

  return (
    <Dialog
      trigger={trigger}
      fullWidth
      maxWidth="sm"
      showCloseButton
      classes={{ paper: dialogClasses.dialogPaper }}
    >
      {() => (
        <>
          <DialogTitle
            disableTypography
            classes={{
              root: `${dialogClasses.titleRoot} ${classes.titleRoot}`,
            }}
          >
            <Typography component="h2" className={dialogClasses.title}>
              Export your PGA events
            </Typography>
            <Typography className={dialogClasses.bodyText}>
              Keep your lessons, events and blocks in sync with your personal
              calendar.
            </Typography>
          </DialogTitle>
          <DialogContent classes={{ root: dialogClasses.content }}>
            <Box className={classes.optionGroup}>
              <IncludeOption
                checked={includeBlockedTimes}
                onChange={event => setIncludeBlockedTimes(event.target.checked)}
                label="Include blocked times"
                description="Show personal blocks alongside booked sessions."
              />
              {user.jrlSignedUp && (
                <IncludeOption
                  checked={includeJrlEvents}
                  onChange={event => setIncludeJrlEvents(event.target.checked)}
                  label="Include Jr. League events"
                  description="Add PGA Jr. League events to your calendar feed."
                />
              )}
              {user.familyCupSignedUp && (
                <IncludeOption
                  checked={includeFgEvents}
                  onChange={event => setIncludeFgEvents(event.target.checked)}
                  label="Include Family Golf events"
                  description="Add Family Golf events to your calendar feed."
                />
              )}
            </Box>
            <WebcalLinks
              includeBlockedTimes={includeBlockedTimes}
              includeJrlEvents={includeJrlEvents}
              includeFgEvents={includeFgEvents}
            />
          </DialogContent>
        </>
      )}
    </Dialog>
  )
}

SubscribeDialog.propTypes = {
  trigger: PropTypes.node.isRequired,
}

export default SubscribeDialog
